import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

/**
 * Format date in user's local timezone
 * @param date - Date (can be UTC string from database, Date object, or timestamp)
 * @returns Formatted date string in local timezone
 * @deprecated Use useDateTimeFormat hook instead for better i18n support
 */
export function formatDate(date: Date | string | number) {
    // Convert to Date object (handles UTC strings automatically)
    const localDate = new Date(date);

    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(localDate);
}

/**
 * Format relative time (e.g., "2h ago", "3d ago")
 * @param date - Date (can be UTC string from database, Date object, or timestamp)
 * @returns Relative time string
 * @deprecated Use useDateTimeFormat hook instead for better i18n support
 */
export function formatRelativeTime(date: Date | string | number) {
    const now = new Date();
    const target = new Date(date); // Automatically converts UTC to local
    const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return formatDate(date);
}

export function generateId() {
    return Math.random().toString(36).substr(2, 9)
}

/**
 * Calls `func` once `wait` ms have passed without another call. `cancel()` drops a call still
 * waiting: a component must cancel on unmount, or the call lands after it is gone - a state update
 * on an unmounted page, and in the test run an uncaught "window is not defined" once the page's
 * environment is torn down (21 Oct 2026, ContactListPage).
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const debounced = (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
    }
    return Object.assign(debounced, { cancel: () => { clearTimeout(timeout); timeout = undefined } })
}






